/*
  Styles for the long-form legal pages (/privacy, /terms).

  Same brand tokens as the app landing page — teal → indigo, Inter / Poppins /
  DM Sans, the pale-sky page gradient — re-tuned for reading: one measured
  column, a sticky contents rail on wide screens, and a sky band at the top so
  the floating nav pill has something to be transparent over.

  Navbar, drawer and footer styling is NOT here — it lives in SiteChrome.
*/
export const legalStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --lg-teal: #2bb3a3;
    --lg-indigo: #6366f1;
    --lg-ink: #0d2437;
    --lg-body: #4d6b80;
    --lg-rule: rgba(13,36,55,0.08);
    --nav-height: 96px;

    /* Same five-rung ladder as the app landing page:
         <= 479px   small phone
         480-733    large phone / phablet
         734-1068   tablet
         1069-1441  laptop / small desktop
         >= 1442    large desktop
       Structural changes only on those lines; everything else rides clamp(). */
    --lg-gutter: clamp(16px, 4vw, 60px);
    --lg-safe-l: env(safe-area-inset-left, 0px);
    --lg-safe-r: env(safe-area-inset-right, 0px);
  }

  html { scroll-behavior: smooth; }

  body {
    font-family: 'Inter', 'DM Sans', sans-serif;
    background: #f2fbff;
    color: var(--lg-ink);
    overflow-x: hidden;
  }

  #root, #__next {
    display: block !important;
    width: 100% !important;
    overflow-x: hidden !important;
    overflow-x: clip !important;
  }

  ::selection { background: rgba(43,179,163,0.25); }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #e0f2fe; }
  ::-webkit-scrollbar-thumb { background: var(--lg-teal); border-radius: 3px; }

  /* ═══ PAGE SHELL ═══ */
  .lg-page {
    display: block;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    overflow-x: clip;
    background:
      linear-gradient(180deg,
        #eef8ff 0%,
        #f4fbff 22%,
        #ffffff 48%,
        #f2fafe 74%,
        #e9f5ff 100%);
    position: relative;
  }

  /* ═══ SKY BAND ═══
     The pill nav carries a white, shadowed state while it is over the hero and
     flips to glass once it clears it, so the band has to be dark enough at the
     top for white type and pale enough at the bottom to hand off to the page. */
  .lg-hero {
    position: relative;
    width: 100%;
    padding:
      calc(var(--nav-height) + clamp(26px, 4vw, 56px))
      calc(var(--lg-gutter) + var(--lg-safe-r))
      clamp(34px, 5vw, 62px)
      calc(var(--lg-gutter) + var(--lg-safe-l));
    background: linear-gradient(165deg, #5ec4f5 0%, #7fd2f6 38%, #bfe8fb 72%, #eef8ff 100%);
    overflow: hidden;
    isolation: isolate;
  }
  .lg-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 32%;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(238,248,255,0) 0%, rgba(238,248,255,0.7) 60%, #eef8ff 100%);
  }
  .lg-hero-inner {
    position: relative;
    z-index: 1;
    max-width: 1180px;
    margin: 0 auto;
  }
  .lg-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: clamp(11px, 1vw, 13px);
    font-weight: 700;
    letter-spacing: 2.2px;
    text-transform: uppercase;
    color: #08415c;
    background: rgba(255,255,255,0.42);
    border: 1px solid rgba(255,255,255,0.6);
    border-radius: 9999px;
    padding: 7px 16px;
    margin-bottom: clamp(14px, 2vw, 22px);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
  .lg-hero h1 {
    font-family: 'Poppins', sans-serif;
    font-size: clamp(30px, 5.4vw, 60px);
    font-weight: 700;
    line-height: 1.08;
    letter-spacing: -1px;
    color: #06293c;
    margin-bottom: clamp(10px, 1.4vw, 16px);
  }
  .lg-hero h1 span {
    display: block;
    background: linear-gradient(90deg, #0d6e77, #3f4fd6);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .lg-hero-sub {
    font-size: clamp(15px, 1.5vw, 19px);
    line-height: 1.65;
    color: #0b3d55;
    max-width: 780px;
    margin-bottom: clamp(18px, 2.4vw, 28px);
  }
  .lg-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .lg-meta span {
    font-size: clamp(12px, 1.1vw, 14px);
    font-weight: 600;
    color: #08415c;
    background: rgba(255,255,255,0.5);
    border: 1px solid rgba(255,255,255,0.65);
    border-radius: 9999px;
    padding: 8px 15px;
  }

  /* ═══ BODY LAYOUT ═══ */
  .lg-main {
    position: relative;
    z-index: 1;
    max-width: 1180px;
    margin: 0 auto;
    padding:
      clamp(28px, 4vw, 54px)
      calc(var(--lg-gutter) + var(--lg-safe-r))
      clamp(64px, 9vw, 118px)
      calc(var(--lg-gutter) + var(--lg-safe-l));
    display: grid;
    grid-template-columns: 262px minmax(0, 1fr);
    gap: clamp(28px, 4vw, 64px);
    align-items: start;
  }
  /* Tablet and below: the rail becomes a collapsed disclosure above the text. */
  @media (max-width: 1068px) {
    .lg-main { grid-template-columns: minmax(0, 1fr); }
  }

  /* ── Contents rail ── */
  .lg-toc {
    position: sticky;
    top: calc(var(--nav-height) + 16px);
    max-height: calc(100vh - var(--nav-height) - 40px);
    overflow-y: auto;
    background: rgba(255,255,255,0.72);
    border: 1px solid rgba(43,179,163,0.16);
    border-radius: 20px;
    padding: 20px 18px;
    box-shadow: 0 10px 30px rgba(15,76,92,0.06);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }
  .lg-toc-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--lg-teal);
    margin-bottom: 14px;
  }
  .lg-toc ol { list-style: none; display: flex; flex-direction: column; gap: 2px; }
  .lg-toc a {
    display: flex;
    gap: 9px;
    font-size: 13.5px;
    line-height: 1.45;
    font-weight: 500;
    color: var(--lg-body);
    text-decoration: none;
    padding: 7px 9px;
    border-radius: 10px;
    transition: background 0.2s, color 0.2s;
  }
  .lg-toc a b {
    flex: none;
    width: 20px;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    color: rgba(13,36,55,0.3);
  }
  .lg-toc a:hover { background: rgba(43,179,163,0.09); color: var(--lg-ink); }
  .lg-toc a:hover b { color: var(--lg-teal); }
  /* The rail collapses into this on tablet/phone. <details> so it is closed by
     default and costs nothing to scroll past. */
  .lg-toc-mobile { display: none; }
  @media (max-width: 1068px) {
    .lg-toc { display: none; }
    .lg-toc-mobile {
      display: block;
      background: rgba(255,255,255,0.78);
      border: 1px solid rgba(43,179,163,0.16);
      border-radius: 18px;
      box-shadow: 0 8px 24px rgba(15,76,92,0.06);
      margin-bottom: clamp(24px, 4vw, 34px);
      overflow: hidden;
    }
    .lg-toc-mobile > summary {
      list-style: none;
      cursor: pointer;
      padding: 16px 18px;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 1.4px;
      text-transform: uppercase;
      color: var(--lg-teal);
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }
    .lg-toc-mobile > summary::-webkit-details-marker { display: none; }
    .lg-toc-mobile > summary::after {
      content: '+';
      font-size: 20px;
      line-height: 1;
      color: var(--lg-body);
    }
    .lg-toc-mobile[open] > summary::after { content: '−'; }
    .lg-toc-mobile ol { list-style: none; padding: 0 10px 12px; }
    .lg-toc-mobile a {
      display: flex;
      gap: 9px;
      font-size: 14px;
      line-height: 1.5;
      color: var(--lg-body);
      text-decoration: none;
      padding: 9px 10px;
      border-radius: 10px;
    }
    .lg-toc-mobile a b {
      flex: none; width: 22px; font-weight: 700;
      font-variant-numeric: tabular-nums;
      color: rgba(13,36,55,0.3);
    }
  }

  /* ── Reading column ── */
  .lg-doc { min-width: 0; }

  .lg-intro {
    background: rgba(255,255,255,0.8);
    border: 1px solid rgba(43,179,163,0.16);
    border-left: 4px solid var(--lg-teal);
    border-radius: 18px;
    padding: clamp(20px, 2.6vw, 32px);
    margin-bottom: clamp(34px, 4.6vw, 58px);
    box-shadow: 0 10px 30px rgba(15,76,92,0.05);
  }
  .lg-intro p {
    font-size: clamp(15px, 1.35vw, 17.5px);
    line-height: 1.78;
    color: var(--lg-body);
  }
  .lg-intro p + p { margin-top: 14px; }

  .lg-section { scroll-margin-top: calc(var(--nav-height) + 18px); }
  .lg-section + .lg-section { margin-top: clamp(38px, 5vw, 62px); }

  .lg-section-head {
    display: flex;
    align-items: flex-start;
    gap: clamp(12px, 1.6vw, 18px);
    padding-bottom: 16px;
    margin-bottom: clamp(16px, 2vw, 22px);
    border-bottom: 1px solid var(--lg-rule);
  }
  .lg-num {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: clamp(32px, 3.4vw, 40px);
    height: clamp(32px, 3.4vw, 40px);
    border-radius: 12px;
    background: linear-gradient(135deg, var(--lg-teal), var(--lg-indigo));
    color: #fff;
    font-size: clamp(13px, 1.2vw, 15px);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    box-shadow: 0 6px 18px rgba(43,179,163,0.28);
  }
  .lg-section-head h2 {
    font-family: 'Poppins', sans-serif;
    font-size: clamp(19px, 2.3vw, 28px);
    font-weight: 600;
    line-height: 1.28;
    letter-spacing: -0.4px;
    color: var(--lg-ink);
    padding-top: clamp(2px, 0.4vw, 5px);
  }

  .lg-section h3 {
    font-family: 'Inter', sans-serif;
    font-size: clamp(15px, 1.5vw, 18px);
    font-weight: 700;
    letter-spacing: -0.1px;
    color: var(--lg-ink);
    margin: clamp(22px, 2.6vw, 30px) 0 10px;
  }
  .lg-section h3:first-child { margin-top: 0; }

  .lg-section p {
    font-size: clamp(15px, 1.35vw, 17px);
    line-height: 1.8;
    color: var(--lg-body);
  }
  .lg-section p + p { margin-top: 13px; }

  .lg-section ul {
    list-style: none;
    margin: 14px 0 0;
    display: flex;
    flex-direction: column;
    gap: 9px;
  }
  .lg-section ul li {
    position: relative;
    padding-left: 24px;
    font-size: clamp(14.5px, 1.3vw, 16.5px);
    line-height: 1.72;
    color: var(--lg-body);
  }
  .lg-section ul li::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 0.72em;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--lg-teal), var(--lg-indigo));
  }
  .lg-section p + ul, .lg-section ul + p { margin-top: 14px; }

  .lg-section a { color: var(--lg-teal); font-weight: 600; text-decoration: none; }
  .lg-section a:hover { text-decoration: underline; }

  /* A callout — used for the emergency notice and the "not a medical device"
     kind of statement that should not read as one more paragraph. */
  .lg-note {
    background: linear-gradient(135deg, rgba(43,179,163,0.08), rgba(99,102,241,0.07));
    border: 1px solid rgba(43,179,163,0.22);
    border-radius: 16px;
    padding: clamp(16px, 2vw, 22px);
    margin-top: 16px;
  }
  .lg-note p {
    font-size: clamp(14.5px, 1.3vw, 16.5px);
    line-height: 1.72;
    color: #0d3f4d;
    font-weight: 500;
  }

  /* Contact / address block */
  .lg-card {
    background: rgba(255,255,255,0.86);
    border: 1px solid rgba(43,179,163,0.18);
    border-radius: 18px;
    padding: clamp(18px, 2.2vw, 26px);
    margin-top: 16px;
    box-shadow: 0 10px 28px rgba(15,76,92,0.05);
  }
  .lg-card dl {
    display: grid;
    grid-template-columns: minmax(120px, 190px) minmax(0, 1fr);
    gap: 10px clamp(14px, 2vw, 24px);
  }
  @media (max-width: 479px) {
    .lg-card dl { grid-template-columns: minmax(0, 1fr); gap: 2px; }
    .lg-card dd { margin-bottom: 12px; }
  }
  .lg-card dt {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: var(--lg-teal);
    padding-top: 3px;
  }
  .lg-card dd {
    font-size: clamp(14.5px, 1.3vw, 16.5px);
    line-height: 1.65;
    color: var(--lg-ink);
    font-weight: 500;
    white-space: pre-line;
    overflow-wrap: break-word;
  }

  /* Closing commitment panel */
  .lg-closing {
    margin-top: clamp(44px, 6vw, 74px);
    background: linear-gradient(135deg, #0a2c3d 0%, #124a5c 52%, #2a3f8f 100%);
    border-radius: clamp(20px, 2.6vw, 28px);
    padding: clamp(26px, 3.6vw, 46px);
    color: #fff;
    box-shadow: 0 20px 50px rgba(10,44,61,0.22);
  }
  .lg-closing h2 {
    font-family: 'Poppins', sans-serif;
    font-size: clamp(20px, 2.4vw, 30px);
    font-weight: 600;
    letter-spacing: -0.4px;
    margin-bottom: 14px;
  }
  .lg-closing p {
    font-size: clamp(15px, 1.35vw, 17px);
    line-height: 1.78;
    color: rgba(255,255,255,0.74);
  }
  .lg-closing ul {
    list-style: none;
    margin-top: 20px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(248px, 1fr));
    gap: 10px 26px;
  }
  .lg-closing ul li {
    position: relative;
    padding-left: 24px;
    font-size: clamp(14.5px, 1.3vw, 16px);
    line-height: 1.65;
    color: rgba(255,255,255,0.84);
  }
  .lg-closing ul li::before {
    content: '';
    position: absolute;
    left: 2px;
    top: 0.6em;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #41e0cc, #8b8ff8);
  }

  /* Cross-link to the sibling document */
  .lg-crosslink {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-top: clamp(28px, 3.4vw, 40px);
    background: rgba(255,255,255,0.8);
    border: 1px solid rgba(43,179,163,0.16);
    border-radius: 18px;
    padding: clamp(16px, 2vw, 22px) clamp(18px, 2.2vw, 26px);
  }
  .lg-crosslink p {
    font-size: clamp(14.5px, 1.3vw, 16.5px);
    color: var(--lg-body);
    line-height: 1.6;
  }
  .lg-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex: none;
    padding: 12px 22px;
    border-radius: 9999px;
    background: linear-gradient(135deg, var(--lg-teal), var(--lg-indigo));
    color: #fff;
    font-size: clamp(14px, 1.2vw, 15.5px);
    font-weight: 600;
    text-decoration: none;
    border: none;
    cursor: pointer;
    box-shadow: 0 10px 26px rgba(43,179,163,0.3);
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .lg-btn:hover { transform: translateY(-2px); box-shadow: 0 14px 32px rgba(43,179,163,0.38); }

  @media (prefers-reduced-motion: reduce) {
    html { scroll-behavior: auto; }
    .lg-btn:hover { transform: none; }
  }
`;
